import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import { getAttendanceExportEmployees } from "../api/asistenciaApi";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function fmtYMD(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function addDays(date, amount) {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

function startOfWeekMonday(value) {
  const date = value ? new Date(`${String(value).slice(0, 10)}T00:00:00`) : new Date();
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return fmtYMD(date);
}

function endOfWeekSunday(mondayYmd) {
  return fmtYMD(addDays(new Date(`${mondayYmd}T00:00:00`), 6));
}

function countWeeks(startYmd, endMondayYmd) {
  if (!startYmd || !endMondayYmd) return 0;
  const start = new Date(`${startYmd}T00:00:00`);
  const end = new Date(`${endMondayYmd}T00:00:00`);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return 0;
  if (end < start) return 0;
  return Math.floor((end.getTime() - start.getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1;
}

function idsFrom(items) {
  return (items || []).map((item) => String(item.id)).filter(Boolean);
}

function renderSelectedNames(selected, options, placeholder) {
  const selectedSet = new Set((selected || []).map(String));
  const labels = (options || [])
    .filter((option) => selectedSet.has(String(option.id)))
    .map((option) => option.name);

  return labels.length ? labels.join(", ") : placeholder;
}

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "14px" },
};

export default function AttendanceExportPdfModal({
  open,
  onClose,
  onDownload,
  downloading = false,
  licenseId,
  selectedDate,
  branches = [],
  areas = [],
  initialBranchId = "",
  initialAreaId = "",
  search = "",
}) {
  const [fromWeek, setFromWeek] = useState("");
  const [toWeek, setToWeek] = useState("");
  const [applyAll, setApplyAll] = useState(true);
  const [selectedBranchIds, setSelectedBranchIds] = useState([]);
  const [selectedAreaIds, setSelectedAreaIds] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!open) return;

    const weekStart = startOfWeekMonday(selectedDate ? fmtYMD(selectedDate) : fmtYMD(new Date()));
    const hasBranchFilter = Boolean(initialBranchId);
    const hasAreaFilter = Boolean(initialAreaId);

    setFromWeek(weekStart);
    setToWeek(weekStart);
    setApplyAll(!hasBranchFilter && !hasAreaFilter);
    setSelectedBranchIds(hasBranchFilter ? [String(initialBranchId)] : idsFrom(branches));
    setSelectedAreaIds(hasAreaFilter ? [String(initialAreaId)] : idsFrom(areas));
    setEmployees([]);
    setSelectedEmployeeIds([]);
    setLoadError("");
  }, [open, selectedDate, initialBranchId, initialAreaId, branches, areas]);

  const weeksCount = useMemo(() => countWeeks(fromWeek, toWeek), [fromWeek, toWeek]);
  const toDate = useMemo(() => (toWeek ? endOfWeekSunday(toWeek) : ""), [toWeek]);
  const exceedsWeeks = weeksCount > 8;

  useEffect(() => {
    if (!open || !licenseId) return;

    if (!applyAll && (!selectedBranchIds.length || !selectedAreaIds.length)) {
      setEmployees([]);
      setSelectedEmployeeIds([]);
      setLoadError("");
      return;
    }

    const ac = new AbortController();

    (async () => {
      try {
        setLoadingEmployees(true);
        setLoadError("");

        const data = await getAttendanceExportEmployees(
          {
            licenseId,
            fromDate: fromWeek || undefined,
            toDate: toDate || undefined,
            branchIds: applyAll ? undefined : selectedBranchIds.join(","),
            areaIds: applyAll ? undefined : selectedAreaIds.join(","),
            search: search?.trim() || undefined,
          },
          { signal: ac.signal }
        );

        const list = Array.isArray(data) ? data : [];
        setEmployees(list);
        setSelectedEmployeeIds(list.map((employee) => String(employee.id)));
      } catch (err) {
        if (err?.name !== "AbortError") {
          setEmployees([]);
          setSelectedEmployeeIds([]);
          setLoadError(err?.message || "No se pudieron cargar los empleados.");
        }
      } finally {
        setLoadingEmployees(false);
      }
    })();

    return () => ac.abort();
  }, [open, licenseId, fromWeek, toDate, applyAll, selectedBranchIds, selectedAreaIds, search]);

  const handleApplyAll = (event) => {
    const checked = event.target.checked;
    setApplyAll(checked);
    if (checked) {
      setSelectedBranchIds(idsFrom(branches));
      setSelectedAreaIds(idsFrom(areas));
    }
  };

  const handleBranches = (event) => {
    const value = Array.isArray(event.target.value)
      ? event.target.value
      : String(event.target.value).split(",").filter(Boolean);
    setApplyAll(false);
    setSelectedBranchIds(value.map(String));
  };

  const handleAreas = (event) => {
    const value = Array.isArray(event.target.value)
      ? event.target.value
      : String(event.target.value).split(",").filter(Boolean);
    setApplyAll(false);
    setSelectedAreaIds(value.map(String));
  };

  const toggleEmployee = (employeeId) => {
    const id = String(employeeId);
    setSelectedEmployeeIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const disabled =
    downloading ||
    loadingEmployees ||
    !fromWeek ||
    !toWeek ||
    weeksCount < 1 ||
    exceedsWeeks ||
    selectedEmployeeIds.length === 0;

  const summary = fromWeek && toDate
    ? `Rango: ${fromWeek} al ${toDate} - ${weeksCount} semanas - ${selectedEmployeeIds.length} empleados seleccionados`
    : `Rango: fecha inicio al fecha fin - 0 semanas - ${selectedEmployeeIds.length} empleados seleccionados`;

  const handleDownload = () => {
    if (disabled) return;

    onDownload?.({
      fromDate: fromWeek,
      toDate,
      branchIds: applyAll ? "" : selectedBranchIds.join(","),
      areaIds: applyAll ? "" : selectedAreaIds.join(","),
      employeeIds: selectedEmployeeIds.join(","),
      search: search?.trim() || "",
    });
  };

  return (
    <Dialog
      open={open}
      onClose={downloading ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "22px",
          width: "min(900px, 94vw)",
        },
      }}
    >
      <Box sx={{ p: 2.2, position: "relative" }}>
        <IconButton
          onClick={onClose}
          disabled={downloading}
          sx={{ position: "absolute", top: 8, right: 8, width: 32, height: 32 }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>

        <Typography sx={{ fontSize: 13, color: "primary.900", pr: 4, mb: 1.5 }}>
          Genera un resumen de asistencia por empleado, agrupado por area, dentro del rango de semanas
        </Typography>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <Stack spacing={1.25} sx={{ width: { xs: "100%", md: "38%" }, flexShrink: 0 }}>
            <TextField
              label="Semana inicial"
              type="date"
              value={fromWeek}
              onChange={(event) => setFromWeek(startOfWeekMonday(event.target.value))}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={fieldSx}
            />

            <TextField
              label="Semana final"
              type="date"
              value={toWeek}
              onChange={(event) => setToWeek(startOfWeekMonday(event.target.value))}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={fieldSx}
            />

            <Typography sx={{ fontSize: 10, color: exceedsWeeks ? "error.main" : "text.secondary" }}>
              *El rango maximo permitido es de 8 semanas*
            </Typography>

            <Typography sx={{ fontSize: 12, fontWeight: 800, color: "primary.900" }}>
              Alcance
            </Typography>

            <FormGroup>
              <FormControlLabel
                control={<Checkbox size="small" checked={applyAll} onChange={handleApplyAll} />}
                label={<Typography sx={{ fontSize: 12 }}>Aplicar a toda la empresa</Typography>}
              />
            </FormGroup>

            <TextField
              select
              size="small"
              value={selectedBranchIds}
              onChange={handleBranches}
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: (selected) =>
                  renderSelectedNames(selected, branches, "Selecciona sucursales"),
              }}
              sx={fieldSx}
            >
              {branches.map((branch) => (
                <MenuItem key={branch.id} value={String(branch.id)}>
                  <Checkbox size="small" checked={selectedBranchIds.includes(String(branch.id))} />
                  {branch.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              value={selectedAreaIds}
              onChange={handleAreas}
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: (selected) =>
                  renderSelectedNames(selected, areas, "Selecciona areas"),
              }}
              sx={fieldSx}
            >
              {areas.map((area) => (
                <MenuItem key={area.id} value={String(area.id)}>
                  <Checkbox size="small" checked={selectedAreaIds.includes(String(area.id))} />
                  {area.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                height: 258,
                overflowY: "auto",
                pr: 0.75,
                borderRadius: "12px",
              }}
            >
              <Stack spacing={0.75}>
                {loadingEmployees ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ height: 120 }}>
                    <CircularProgress size={24} />
                  </Stack>
                ) : null}

                {!loadingEmployees && loadError ? (
                  <Typography sx={{ fontSize: 12, color: "error.main", fontWeight: 700 }}>
                    {loadError}
                  </Typography>
                ) : null}

                {!loadingEmployees && !loadError && employees.map((employee) => {
                  const checked = selectedEmployeeIds.includes(String(employee.id));
                  const branchLabel = (employee.branches || []).map((branch) => branch.name).join(", ");
                  const areaLabel = (employee.areas || []).map((area) => area.name).join(", ");

                  return (
                    <Box
                      key={employee.id}
                      onClick={() => toggleEmployee(employee.id)}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "28px 1fr 92px 90px",
                        alignItems: "center",
                        gap: 1,
                        minHeight: 34,
                        px: 1,
                        borderRadius: "8px",
                        bgcolor: checked ? "rgba(176, 187, 222, 0.72)" : "rgba(176, 187, 222, 0.35)",
                        cursor: "pointer",
                      }}
                    >
                      <Checkbox size="small" checked={checked} sx={{ p: 0 }} />
                      <Typography noWrap sx={{ fontSize: 11, fontWeight: 900, color: "primary.900" }}>
                        {employee.fullName}
                      </Typography>
                      <Typography noWrap sx={{ fontSize: 9.5, fontWeight: 700, color: "neutral.700" }}>
                        {branchLabel || "-"}
                      </Typography>
                      <Typography noWrap sx={{ fontSize: 9.5, fontWeight: 700, color: "neutral.700" }}>
                        {areaLabel || "-"}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Box>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mt: 1.5 }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: exceedsWeeks ? "error.main" : "secondary.700" }}>
            {summary}
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button
              onClick={onClose}
              disabled={downloading}
              sx={{ textTransform: "none", fontWeight: 800, color: "primary.900" }}
            >
              Cancelar
            </Button>

            <Button
              onClick={handleDownload}
              disabled={disabled}
              startIcon={<PictureAsPdfIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 900,
                borderRadius: "18px",
                px: 1.8,
                bgcolor: "primary.900",
                color: "logoblanco",
                "&:hover": { bgcolor: "primary.800" },
                "&.Mui-disabled": {
                  bgcolor: "neutral.300",
                  color: "neutral.600",
                },
              }}
            >
              {downloading ? "Descargando..." : "Descargar"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
}
